import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import FileDownloadRoundedIcon from "@mui/icons-material/FileDownloadRounded";
import PrintRoundedIcon from "@mui/icons-material/PrintRounded";
import DeleteOutlineRoundedIcon from "@mui/icons-material/DeleteOutlineRounded";
import { useNavigate } from "react-router-dom";
import { adminApi } from "../api/client";
import { ResourceListItem } from "../types";

const statusOptions = ["draft", "pending", "approved", "granted", "rejected", "cancelled", "expired"];
const currencyFormatter = new Intl.NumberFormat("en-IN", {
  style: "currency",
  currency: "INR",
  maximumFractionDigits: 0
});

function parsePayload(value: unknown) {
  if (!value) {
    return {};
  }

  if (typeof value === "string") {
    try {
      return JSON.parse(value) as Record<string, any>;
    } catch (error) {
      return {};
    }
  }

  return value as Record<string, any>;
}

function getDraft(item: ResourceListItem) {
  const payload = parsePayload(item.payload);
  return (payload.draft || payload || {}) as Record<string, any>;
}

function getCustomQuotationName(item: ResourceListItem) {
  const draft = getDraft(item);
  return [draft.eventGroom, draft.eventBride].filter(Boolean).join(" & ") || draft.eventOccasion || "-";
}

function getCustomQuotationFamily(item: ResourceListItem) {
  return getDraft(item).eventFamily || "-";
}

function getCustomQuotationEventDate(item: ResourceListItem) {
  const eventDate = getDraft(item).eventDate;
  return eventDate ? String(eventDate).slice(0, 10) : "-";
}

function getCustomQuotationMobile(item: ResourceListItem) {
  const draft = getDraft(item);
  return String(item.client_mobile || draft.clientMobile || "").replace(/\D/g, "") || "-";
}

function getCustomQuotationType(item: ResourceListItem) {
  const draft = getDraft(item);
  return String(item.quotation_type || draft.quotationType || "Ganpati Bappa Decoration");
}

function getCustomQuotationDecorType(item: ResourceListItem) {
  const draft = getDraft(item);
  return String(
    draft.eventDecorType ||
      draft.event_decor_type_label ||
      draft.headerConfig?.event_decor_type_label ||
      draft.header_config?.event_decor_type_label ||
      item.decor_type_label ||
      "-"
  );
}

function getCustomQuotationPaymentMethod(item: ResourceListItem) {
  const draft = getDraft(item);
  return String(item.payment_method || draft.paymentMethod || "Cash");
}

function getCustomQuotationPaymentTransactions(item: ResourceListItem) {
  const draft = getDraft(item);
  const raw = item.payment_transactions || draft.paymentTransactions || [];
  const rows = typeof raw === "string" ? parsePayload(raw) : raw;
  return Array.isArray(rows) ? rows : [];
}

function getCustomQuotationPaidAmount(item: ResourceListItem) {
  const transactions = getCustomQuotationPaymentTransactions(item);
  if (transactions.length) {
    return transactions.reduce((sum, transaction: any) => sum + Number(transaction.amountPaid ?? transaction.amount_paid ?? transaction.amount ?? 0), 0);
  }

  const draft = getDraft(item);
  return Number(item.partial_amount_paid ?? draft.partialAmountPaid ?? 0);
}

function getCustomQuotationPaymentMethodsLabel(item: ResourceListItem) {
  const transactions = getCustomQuotationPaymentTransactions(item);
  const uniqueMethods = transactions.reduce<string[]>((methods, transaction: any) => {
    const method = String(transaction.paymentMethod || transaction.payment_method || "").trim();
    if (method && !methods.includes(method)) {
      methods.push(method);
    }
    return methods;
  }, []);

  if (uniqueMethods.length) {
    return uniqueMethods.join(" + ");
  }

  const paymentMethod = getCustomQuotationPaymentMethod(item);
  return ["Cash", "UPI", "Bank Transfer", "Other"].includes(paymentMethod) ? paymentMethod : "-";
}

function getCustomQuotationBalance(item: ResourceListItem) {
  const draft = getDraft(item);
  const paymentMethod = getCustomQuotationPaymentMethod(item);
  if (paymentMethod !== "Partial Paid" && paymentMethod !== "Paid") {
    return 0;
  }

  const totalAmount = Number(item.partial_total_amount ?? draft.partialTotalAmount ?? 0);
  const amountPaid = getCustomQuotationPaidAmount(item);
  return Math.max(totalAmount - amountPaid, 0);
}

function getCustomQuotationStatus(item: ResourceListItem) {
  const paymentMethod = getCustomQuotationPaymentMethod(item);
  if (paymentMethod === "Paid") {
    return "granted";
  }

  if (paymentMethod === "Partial Paid") {
    return "pending";
  }

  return String(item.status || getDraft(item).status || "draft").toLowerCase();
}

function formatStatusLabel(value: unknown) {
  const status = String(value || "draft").replace(/_/g, " ");
  return status.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getStatusDisplayColor(value: unknown) {
  const status = String(value || "").toLowerCase();
  if (status === "granted") {
    return "success.main";
  }

  if (status === "pending") {
    return "warning.dark";
  }

  return "text.primary";
}

function getStatusChipSx(value: unknown) {
  const status = String(value || "").toLowerCase();
  if (status === "granted") {
    return {
      bgcolor: "rgba(32, 114, 69, 0.12)",
      borderColor: "rgba(32, 114, 69, 0.26)",
      color: "#207245"
    };
  }

  if (status === "pending") {
    return {
      bgcolor: "rgba(198, 83, 9, 0.12)",
      borderColor: "rgba(198, 83, 9, 0.25)",
      color: "#c65309"
    };
  }

  return {
    bgcolor: "rgba(107, 74, 32, 0.1)",
    borderColor: "rgba(107, 74, 32, 0.18)",
    color: "#6b4a20"
  };
}

function getPaymentChipSx(value: unknown) {
  const paymentMethod = String(value || "").toLowerCase();
  if (paymentMethod === "paid") {
    return {
      bgcolor: "rgba(32, 114, 69, 0.12)",
      borderColor: "rgba(32, 114, 69, 0.24)",
      color: "#207245"
    };
  }

  if (paymentMethod === "partial paid") {
    return {
      bgcolor: "rgba(154, 91, 18, 0.12)",
      borderColor: "rgba(154, 91, 18, 0.24)",
      color: "#9a5b12"
    };
  }

  return {
    bgcolor: "rgba(72, 57, 42, 0.08)",
    borderColor: "rgba(72, 57, 42, 0.16)",
    color: "#48392a"
  };
}

function formatDisplayDate(value: string) {
  if (!value || value === "-") {
    return "-";
  }

  const [year, month, day] = value.slice(0, 10).split("-");
  return year && month && day ? `${day}-${month}-${year}` : value;
}

function isEnabled(value: unknown) {
  return value === undefined || value === null || value === true || value === 1;
}

function calculateItemTotal(item: any) {
  if (item.lineTotal !== undefined && item.lineTotal !== null) {
    return Number(item.lineTotal || 0);
  }

  if (item.line_total !== undefined && item.line_total !== null) {
    return Number(item.line_total || 0);
  }

  const quantity = Number(item.quantity || 0);
  const unitRate = Number(item.unitRate ?? item.unit_rate ?? 0);
  const pricingMode = item.pricingMode || item.pricing_mode || "manual";
  const base = pricingMode === "quantity_x_rate" ? quantity * unitRate : unitRate;
  const discountType = item.discountType || item.discount_type || "none";
  const discountValue = Number(item.discountValue ?? item.discount_value ?? 0);
  const discount = discountType === "percent" ? (base * discountValue) / 100 : discountType === "flat" ? discountValue : 0;
  const taxable = Math.max(base - discount, 0);
  return taxable + (taxable * Number(item.taxRate ?? item.tax_rate ?? 0)) / 100;
}

function calculateCustomQuotationTotal(item: ResourceListItem) {
  const draft = getDraft(item);
  const subtotal = (draft.sections || []).reduce((sectionSum: number, section: any) => {
    if (!isEnabled(section.isVisible ?? section.is_visible)) {
      return sectionSum;
    }

    return sectionSum + (section.items || []).reduce((itemSum: number, lineItem: any) => {
      return itemSum + (isEnabled(lineItem.includeInTotal ?? lineItem.include_in_total) && isEnabled(lineItem.isVisible ?? lineItem.is_visible) ? calculateItemTotal(lineItem) : 0);
    }, 0);
  }, 0);
  const adjustments = (draft.adjustments || []).reduce((sum: number, adjustment: any) => {
    if (!draft.showAdjustments) {
      return sum;
    }

    if (!isEnabled(adjustment.isVisible ?? adjustment.is_visible)) {
      return sum;
    }

    if (adjustment.amount !== undefined && adjustment.amount !== null) {
      return sum + Number(adjustment.amount || 0);
    }

    const calculationType = adjustment.calculationType || adjustment.calculation_type || "fixed";
    const calculationValue = Number(adjustment.calculationValue ?? adjustment.calculation_value ?? 0);
    return sum + (calculationType === "percent" ? (subtotal * calculationValue) / 100 : calculationValue);
  }, 0);

  return Math.max(subtotal + adjustments, 0);
}

function getCustomQuotationPendingSummaryAmount(item: ResourceListItem) {
  const paymentMethod = getCustomQuotationPaymentMethod(item);
  if (paymentMethod === "Partial Paid") {
    return getCustomQuotationBalance(item);
  }

  return calculateCustomQuotationTotal(item);
}

function formatExportValue(value: unknown) {
  if (value === null || value === undefined || value === "") {
    return "-";
  }

  if (typeof value === "object") {
    return JSON.stringify(value);
  }

  return String(value);
}

function getCustomQuotationPaymentTransactionsSummary(item: ResourceListItem) {
  const transactions = getCustomQuotationPaymentTransactions(item);
  return transactions.length
    ? transactions
        .map((transaction: any) => {
          const method = transaction.paymentMethod || transaction.payment_method || "-";
          const amount = transaction.amountPaid ?? transaction.amount_paid ?? transaction.amount ?? 0;
          const date = transaction.paymentDate || transaction.payment_date || "-";
          const reference = transaction.referenceNumber || transaction.reference_number;
          return [method, currencyFormatter.format(Number(amount || 0)), formatDisplayDate(String(date).slice(0, 10)), reference ? `Ref: ${reference}` : ""].filter(Boolean).join(" | ");
        })
        .join("; ")
    : "-";
}

function getDraftListSummary(rows: unknown[], labelKey: string, fallbackKey?: string) {
  return Array.isArray(rows) && rows.length
    ? rows.map((row: any) => formatExportValue(row[labelKey] ?? (fallbackKey ? row[fallbackKey] : undefined))).join("; ")
    : "-";
}

function getDraftSectionsSummary(draft: Record<string, any>) {
  const sections = Array.isArray(draft.sections) ? draft.sections : [];
  return sections.length
    ? sections
        .map((section: any) => {
          const itemNames = Array.isArray(section.items)
            ? section.items.map((item: any) => formatExportValue(item.itemName ?? item.item_name)).join(", ")
            : "";
          return [formatExportValue(section.sectionTitle ?? section.section_title), itemNames].filter((value) => value && value !== "-").join(": ");
        })
        .join("; ")
    : "-";
}

function escapeExcelXml(value: unknown) {
  return formatExportValue(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function buildExcelXml(rows: Array<Record<string, unknown>>) {
  const headers = Object.keys(rows[0] || {});
  const worksheetRows = [
    `<Row>${headers.map((header) => `<Cell><Data ss:Type="String">${escapeExcelXml(header)}</Data></Cell>`).join("")}</Row>`,
    ...rows.map((row) => `<Row>${headers.map((header) => `<Cell><Data ss:Type="String">${escapeExcelXml(row[header])}</Data></Cell>`).join("")}</Row>`)
  ].join("");

  return `<?xml version="1.0"?>
<?mso-application progid="Excel.Sheet"?>
<Workbook xmlns="urn:schemas-microsoft-com:office:spreadsheet" xmlns:ss="urn:schemas-microsoft-com:office:spreadsheet">
  <Worksheet ss:Name="Custom Quotations">
    <Table>${worksheetRows}</Table>
  </Worksheet>
</Workbook>`;
}

function downloadExcelFile(rows: Array<Record<string, unknown>>) {
  const today = new Date().toISOString().slice(0, 10);
  const blob = new Blob([buildExcelXml(rows)], {
    type: "application/vnd.ms-excel;charset=utf-8"
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = `custom-quotations-${today}.xls`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

function createCustomQuotationExportRow(item: ResourceListItem) {
  const draft = getDraft(item);
  return {
    "Quote No.": item.quote_number || "-",
    "Family Name": getCustomQuotationFamily(item),
    Name: getCustomQuotationName(item),
    "Mobile No": getCustomQuotationMobile(item),
    "Event Date": formatDisplayDate(getCustomQuotationEventDate(item)),
    "Decor Type": getCustomQuotationDecorType(item),
    Type: getCustomQuotationType(item),
    Title: item.title || draft.title || "-",
    Amount: calculateCustomQuotationTotal(item),
    "Payment Status": getCustomQuotationPaymentMethod(item),
    "Payment Method": getCustomQuotationPaymentMethodsLabel(item),
    Paid: getCustomQuotationPaidAmount(item),
    Balance: getCustomQuotationBalance(item),
    Status: formatStatusLabel(getCustomQuotationStatus(item)),
    "Event Venue": draft.eventVenue || draft.event_venue || "-",
    "Event Occasion": draft.eventOccasion || draft.event_occasion || "-",
    Groom: draft.eventGroom || draft.event_groom_name || "-",
    Bride: draft.eventBride || draft.event_bride_name || "-",
    Sections: getDraftSectionsSummary(draft),
    Adjustments: getDraftListSummary(draft.adjustments, "label"),
    "Payment Transactions": getCustomQuotationPaymentTransactionsSummary(item)
  };
}

export default function CustomQuotationsPage() {
  const navigate = useNavigate();
  const [items, setItems] = useState<ResourceListItem[]>([]);
  const [editingStatusId, setEditingStatusId] = useState<unknown>(null);
  const [filters, setFilters] = useState({
    name: "",
    family: "",
    eventDate: "",
    title: "",
    status: "",
    mobile: "",
    quotationType: "",
    decorType: "",
    paymentMethod: ""
  });
  const [deleteTarget, setDeleteTarget] = useState<ResourceListItem | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [tablePage, setTablePage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await adminApi.listResource<ResourceListItem>("custom-quotations", "?limit=200&sort=id&order=DESC");
      setItems(response.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load custom quotations");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadItems();
  }, []);

  const filteredItems = useMemo(() => {
    const matches = (value: unknown, filter: string) => String(value || "").toLowerCase().includes(filter.trim().toLowerCase());

    return items.filter((item) => {
      const status = getCustomQuotationStatus(item);
      return (
        matches(getCustomQuotationName(item), filters.name) &&
        matches(getCustomQuotationFamily(item), filters.family) &&
        matches(getCustomQuotationEventDate(item), filters.eventDate) &&
        matches(item.title, filters.title) &&
        matches(getCustomQuotationMobile(item), filters.mobile) &&
        matches(getCustomQuotationType(item), filters.quotationType) &&
        matches(getCustomQuotationDecorType(item), filters.decorType) &&
        (matches(getCustomQuotationPaymentMethod(item), filters.paymentMethod) || matches(getCustomQuotationPaymentMethodsLabel(item), filters.paymentMethod)) &&
        (!filters.status || status === filters.status)
      );
    });
  }, [filters, items]);

  const paginatedItems = useMemo(() => {
    const start = tablePage * rowsPerPage;
    return filteredItems.slice(start, start + rowsPerPage);
  }, [filteredItems, rowsPerPage, tablePage]);

  const summary = useMemo<{ totalCount: number; grantedAmount: number; pendingAmount: number }>(() => {
    return items.reduce<{ totalCount: number; grantedAmount: number; pendingAmount: number }>(
      (totals, item) => {
        const status = getCustomQuotationStatus(item);
        const amount = calculateCustomQuotationTotal(item);

        if (status === "granted") {
          totals.grantedAmount += amount;
        }

        if (status === "pending") {
          totals.pendingAmount += getCustomQuotationPendingSummaryAmount(item);
        }

        return totals;
      },
      {
        totalCount: items.length,
        grantedAmount: 0,
        pendingAmount: 0
      }
    );
  }, [items]);

  const updateFilter = (key: keyof typeof filters, value: string) => {
    setFilters((current) => ({
      ...current,
      [key]: value
    }));
    setTablePage(0);
  };
  const hasActiveFilters = Object.values(filters).some(Boolean);

  const clearFilters = () => {
    setFilters({
      name: "",
      family: "",
      eventDate: "",
      title: "",
      status: "",
      mobile: "",
      quotationType: "",
      decorType: "",
      paymentMethod: ""
    });
    setTablePage(0);
  };

  const updateStatus = async (item: ResourceListItem, nextStatus: string) => {
    try {
      setLoading(true);
      setError(null);
      const payload = parsePayload(item.payload);
      const paymentMethod = getCustomQuotationPaymentMethod(item);
      const effectiveStatus = paymentMethod === "Paid" ? "granted" : paymentMethod === "Partial Paid" ? "pending" : nextStatus;
      const draft = {
        ...(payload.draft || payload || {}),
        status: effectiveStatus
      };
      const updated = await adminApi.updateResource<ResourceListItem>("custom-quotations", String(item.id), {
        quote_number: item.quote_number || null,
        title: item.title || draft.title || "Custom Event Quotation",
        status: effectiveStatus,
        client_mobile: item.client_mobile || getCustomQuotationMobile(item).replace("-", ""),
        quotation_type: item.quotation_type || getCustomQuotationType(item),
        payment_method: item.payment_method || paymentMethod,
        partial_total_amount: Number(item.partial_total_amount ?? draft.partialTotalAmount ?? 0),
        partial_amount_paid: getCustomQuotationPaidAmount(item),
        payment_transactions: getCustomQuotationPaymentTransactions(item),
        remaining_balance: getCustomQuotationBalance(item),
        payload: { draft }
      });
      setItems((current) => current.map((row) => (row.id === item.id ? { ...row, ...updated, status: updated.status || effectiveStatus, payload: updated.payload || { draft } } : row)));
      setEditingStatusId(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update custom quotation status");
    } finally {
      setLoading(false);
    }
  };

  const deleteItem = async () => {
    if (!deleteTarget) {
      return;
    }

    try {
      setLoading(true);
      await adminApi.deleteResource("custom-quotations", String(deleteTarget.id));
      setDeleteTarget(null);
      await loadItems();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete custom quotation");
    } finally {
      setLoading(false);
    }
  };

  const exportToExcel = async () => {
    try {
      setExporting(true);
      setError(null);
      const pageSize = 500;
      let page = 1;
      let allItems: ResourceListItem[] = [];
      let total = 0;

      do {
        const response = await adminApi.listResource<ResourceListItem>("custom-quotations", `?limit=${pageSize}&page=${page}&sort=id&order=DESC`);
        allItems = [...allItems, ...response.items];
        total = response.pagination.total;
        page += 1;
      } while (allItems.length < total);

      downloadExcelFile(allItems.map(createCustomQuotationExportRow));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to export custom quotations");
    } finally {
      setExporting(false);
    }
  };

  return (
    <Stack spacing={3}>
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 4,
          border: "1px solid rgba(118, 93, 68, 0.12)",
          background: "linear-gradient(135deg, rgba(255,250,244,0.98), rgba(248,239,225,0.94))",
          boxShadow: "0 18px 45px rgba(84, 59, 35, 0.08)"
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", sm: "center" }}>
          <div>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              Custom Quotations
            </Typography>
            <Typography sx={{ color: "text.secondary" }}>
              Create standalone quotations with the same print-view fields without linking to events, categories, or item master.
            </Typography>
          </div>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25}>
            <Button variant="outlined" startIcon={<FileDownloadRoundedIcon />} onClick={() => void exportToExcel()} disabled={exporting} sx={{ px: 2.5, borderRadius: 2.5, fontWeight: 800 }}>
              {exporting ? "Exporting..." : "Export to Excel"}
            </Button>
            <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => navigate("/admin/custom-quotations/new")} sx={{ px: 2.5, borderRadius: 2.5, fontWeight: 800 }}>
              New
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error ? <Alert severity="error">{error}</Alert> : null}

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "repeat(3, minmax(0, 1fr))" },
          gap: 2
        }}
      >
        {[
          { label: "Total Custom Quotations", value: String(summary.totalCount), accent: "#6b4a20" },
          { label: "Total Granted Amount", value: currencyFormatter.format(summary.grantedAmount), accent: "#207245" },
          { label: "Total Pending Amount", value: currencyFormatter.format(summary.pendingAmount), accent: "#9a5b12" }
        ].map((card) => (
          <Paper
            key={card.label}
            elevation={0}
            sx={{
              p: 2.25,
              minHeight: 104,
              borderRadius: 3,
              border: "1px solid rgba(118, 93, 68, 0.12)",
              background: "rgba(255, 250, 244, 0.96)",
              borderLeft: `5px solid ${card.accent}`,
              boxShadow: "0 12px 28px rgba(84, 59, 35, 0.06)"
            }}
          >
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 0.75 }}>
              {card.label}
            </Typography>
            <Typography variant="h5" sx={{ fontWeight: 800, color: card.accent }}>
              {card.value}
            </Typography>
          </Paper>
        ))}
      </Box>

      <Paper elevation={0} sx={{ p: 2.25, borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.96)" }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ xs: "stretch", md: "center" }} sx={{ mb: 1.75 }}>
          <Box>
            <Typography sx={{ color: "#3b2a1a", fontWeight: 900 }}>Search & Filters</Typography>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>
              Showing {filteredItems.length} of {items.length} custom quotations
            </Typography>
          </Box>
          <Button variant="outlined" onClick={clearFilters} disabled={!hasActiveFilters} sx={{ borderRadius: 2, fontWeight: 800 }}>
            Clear filters
          </Button>
        </Stack>
        <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))", md: "repeat(3, minmax(0, 1fr))", xl: "repeat(4, minmax(0, 1fr))" }, gap: 1.5 }}>
          <TextField size="small" label="Family Name" value={filters.family} onChange={(event) => updateFilter("family", event.target.value)} />
          <TextField size="small" label="Name" value={filters.name} onChange={(event) => updateFilter("name", event.target.value)} />
          <TextField size="small" label="Mobile No" value={filters.mobile} onChange={(event) => updateFilter("mobile", event.target.value)} />
          <TextField size="small" label="Event Date" type="date" InputLabelProps={{ shrink: true }} value={filters.eventDate} onChange={(event) => updateFilter("eventDate", event.target.value)} />
          <TextField size="small" label="Title" value={filters.title} onChange={(event) => updateFilter("title", event.target.value)} />
          <TextField size="small" label="Decor Type" value={filters.decorType} onChange={(event) => updateFilter("decorType", event.target.value)} />
          <TextField size="small" label="Type" value={filters.quotationType} onChange={(event) => updateFilter("quotationType", event.target.value)} />
          <TextField size="small" label="Payment" select value={filters.paymentMethod} onChange={(event) => updateFilter("paymentMethod", event.target.value)}>
            <MenuItem value="">All</MenuItem>
            {["Cash", "UPI", "Partial Paid", "Paid", "Bank Transfer", "Other"].map((method) => (
              <MenuItem key={method} value={method}>
                {method}
              </MenuItem>
            ))}
          </TextField>
          <TextField size="small" label="Status" select value={filters.status} onChange={(event) => updateFilter("status", event.target.value)}>
            <MenuItem value="">All</MenuItem>
            {statusOptions.map((status) => (
              <MenuItem key={status} value={status}>
                {formatStatusLabel(status)}
              </MenuItem>
            ))}
          </TextField>
        </Box>
      </Paper>

      <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 4, border: "1px solid rgba(118, 93, 68, 0.12)", background: "rgba(255, 250, 244, 0.98)", overflowX: "auto" }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            minWidth: 1520,
            "& .MuiTableCell-root": { borderColor: "rgba(118, 93, 68, 0.1)", py: 1.6 },
            "& .MuiTableCell-head": {
              bgcolor: "#fff8eb",
              color: "#6b4a20",
              fontSize: 12,
              fontWeight: 900,
              letterSpacing: "0.08em",
              textTransform: "uppercase"
            },
            "& .MuiTableRow-root:hover .MuiTableCell-body": { bgcolor: "rgba(245, 232, 211, 0.35)" }
          }}
        >
          <TableHead>
            <TableRow>
              <TableCell>Quote No.</TableCell>
              <TableCell>Family Name</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Mobile No</TableCell>
              <TableCell>Event Date</TableCell>
              <TableCell>Decor Type</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Title</TableCell>
              <TableCell align="right">Amount</TableCell>
              <TableCell>Payment Status</TableCell>
              <TableCell>Payment Method</TableCell>
              <TableCell align="right">Paid</TableCell>
              <TableCell align="right">Balance</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Updated</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedItems.map((item) => (
              <TableRow key={String(item.id)}>
                <TableCell>
                  <Typography sx={{ color: "#6b4a20", fontWeight: 900 }}>{String(item.quote_number || "-")}</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: 800 }}>{getCustomQuotationFamily(item)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ fontWeight: 700 }}>{getCustomQuotationName(item)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ whiteSpace: "nowrap" }}>{getCustomQuotationMobile(item)}</Typography>
                </TableCell>
                <TableCell>
                  <Typography sx={{ whiteSpace: "nowrap" }}>{formatDisplayDate(getCustomQuotationEventDate(item))}</Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 160 }}>
                  <Typography noWrap title={getCustomQuotationDecorType(item)}>{getCustomQuotationDecorType(item)}</Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 160 }}>
                  <Typography noWrap title={getCustomQuotationType(item)}>{getCustomQuotationType(item)}</Typography>
                </TableCell>
                <TableCell sx={{ maxWidth: 170 }}>
                  <Typography noWrap title={String(item.title || "-")}>{String(item.title || "-")}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontWeight: 900, whiteSpace: "nowrap" }}>{currencyFormatter.format(calculateCustomQuotationTotal(item))}</Typography>
                </TableCell>
                <TableCell>
                  <Chip label={getCustomQuotationPaymentMethod(item)} size="small" variant="outlined" sx={{ ...getPaymentChipSx(getCustomQuotationPaymentMethod(item)), fontWeight: 800 }} />
                </TableCell>
                <TableCell sx={{ maxWidth: 160 }}>
                  <Typography noWrap title={getCustomQuotationPaymentMethodsLabel(item)}>{getCustomQuotationPaymentMethodsLabel(item)}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>{currencyFormatter.format(getCustomQuotationPaidAmount(item))}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography sx={{ color: getCustomQuotationBalance(item) > 0 ? "#9a5b12" : "#207245", fontWeight: 900, whiteSpace: "nowrap" }}>
                    {currencyFormatter.format(getCustomQuotationBalance(item))}
                  </Typography>
                </TableCell>
                <TableCell onDoubleClick={() => setEditingStatusId(item.id)} sx={{ cursor: "pointer", minWidth: 150 }}>
                  {editingStatusId === item.id ? (
                    <TextField select autoFocus value={getCustomQuotationStatus(item)} onChange={(event) => void updateStatus(item, event.target.value)} onBlur={() => setEditingStatusId(null)} disabled={loading} sx={{ minWidth: 130 }}>
                      {statusOptions.map((status) => (
                        <MenuItem key={status} value={status}>
                          {formatStatusLabel(status)}
                        </MenuItem>
                      ))}
                    </TextField>
                  ) : (
                    <Chip
                      label={formatStatusLabel(getCustomQuotationStatus(item))}
                      size="small"
                      variant="outlined"
                      sx={{ ...getStatusChipSx(getCustomQuotationStatus(item)), fontWeight: 900 }}
                    />
                  )}
                </TableCell>
                <TableCell>
                  <Typography sx={{ whiteSpace: "nowrap" }}>{formatDisplayDate(String(item.updated_at || item.created_at || "-").slice(0, 10))}</Typography>
                </TableCell>
                <TableCell align="right">
                  <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                    <IconButton size="small" onClick={() => navigate(`/admin/custom-quotations/${item.id}/edit`)} title="Edit">
                      <EditRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" onClick={() => navigate(`/admin/custom-quotations/${item.id}/print`)} title="Print">
                      <PrintRoundedIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => setDeleteTarget(item)} title="Delete" disabled={loading}>
                      <DeleteOutlineRoundedIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {!filteredItems.length ? (
              <TableRow>
                <TableCell colSpan={16} align="center" sx={{ py: 5, color: "text.secondary" }}>
                  {loading ? "Loading custom quotations..." : "No custom quotations found."}
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredItems.length}
          page={tablePage}
          rowsPerPage={rowsPerPage}
          rowsPerPageOptions={[10, 25, 50, 100]}
          onPageChange={(_, nextPage) => setTablePage(nextPage)}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(Number(event.target.value));
            setTablePage(0);
          }}
        />
      </TableContainer>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} fullWidth maxWidth="xs">
        <DialogTitle>Delete custom quotation?</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: "text.secondary" }}>
            Are you sure you want to delete custom quotation <strong>{String(deleteTarget?.quote_number || deleteTarget?.title || "")}</strong>? This action will remove it from the active Custom Quotations list.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteTarget(null)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => void deleteItem()} disabled={loading}>
            {loading ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
